import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import type { FC } from 'react';
import { useCallback } from 'react';
import { AltDealCellContent } from './AltDealCellContent';
import { FullWidthGridItem } from './PaymentStructureGrid.styles';
import { SkeletonContent } from './SkeletonContent';
import type { Slot } from './types';
import { UnavailableContent } from './UnavailableContent';
import { useGridActions } from '../contexts';

interface PaymentStructureGridCellProps {
    slot: Slot;
    dealType: 'finance' | 'lease';
}

export const PaymentStructureGridCell: FC<PaymentStructureGridCellProps> = ({ slot, dealType }) => {
    const theme = useInterstateTheme();
    const { onCellSelect, selectedCells, isCellDisabled } = useGridActions();

    const isReady = slot.status === 'ready' && !!slot.data;
    const isSelected = isReady && selectedCells[dealType]?.id === slot.data!.id;
    const isDisabled = isReady && isCellDisabled(slot.data!);
    const isClickable = isReady && !isDisabled;

    const handleClick = useCallback(() => {
        if (isReady && slot.data && !isCellDisabled(slot.data)) {
            onCellSelect(slot.data);
        }
    }, [isReady, slot.data, isCellDisabled, onCellSelect]);

    const renderInner = () => {
        if (slot.status === 'pending') return <SkeletonContent />;
        if (slot.status === 'failed' || slot.status === 'timed-out') {
            return <UnavailableContent />;
        }
        if (isReady) return <AltDealCellContent cell={slot.data!} dealType={dealType} />;
        return null;
    };

    return (
        <FullWidthGridItem
            theme={theme}
            data-testid={`alt-deal-cell-${slot.id}`}
            onClick={isClickable || isSelected ? handleClick : undefined}
            $isSelected={isSelected}
            $isClickable={isClickable}
            $isDisabled={isDisabled}
        >
            {renderInner()}
        </FullWidthGridItem>
    );
};
